import { HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel } from '@microsoft/signalr';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { WorkforceProfileService } from '../../../Component/Api/WorkforceProfile/WorkforceProfileService';
import { MicroLoading } from '../../../Component/Common/Components/Loading/IntermediateLoading';
import { ImportProgressBar } from '../../../Component/Common/Components/ProgressBar/ImportProgressBar';
import config from '../../../config';
import { WorkforceProfilePreview } from './WorkforceProfilePreview';
import { SimpleTab } from '../../../Component/Common/Components/Tabs/SimpleTab';
import React from 'react';
import { TabContext, TabPanel } from '@mui/lab';
import moment from 'moment';

export interface ProgressData {
    progress: number;
    numberOfRowsCorrect: number;
    numberOfRowsFailed: number;
    log: any[];
    isSuccess: boolean;
}

export const WorkforceProfilePage = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const { WSPScorecardId } = useParams<{ WSPScorecardId: string }>();
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const [lastPopulatedDate, setLastPopulatedDate] = useState<Date | null>(null);
    const [progress, setProgress] = useState(0);
    const [keyCounter, setKeyCounter] = useState<number>(0);
    const [progressData, setProgressData] = useState<ProgressData>({
        progress: progress,
        numberOfRowsCorrect: 0,
        numberOfRowsFailed: 0,
        log: [],
        isSuccess: true,
    });

    const routes = [
        `/WorkforceProfile/previewReport`,
        `/WorkforceProfile/previewReportErrors`,
    ];
    const labels = ['Report', 'Errors'];

    const tabsData = routes.map((route, index) => ({
        label: labels[index],
        route: labels[index]
    }));

    const [currentTab, setCurrentTab] = React.useState('Report');

    const handleChange = (newValue: string) => {
        setCurrentTab(newValue);
        setKeyCounter(keyCounter + 1);
    };

    const populateDataUpdate = async () => {
        setIsUpdating(true);
        await WorkforceProfileService.populate(WSPScorecardId);
    }

    const signalR = async () => {
        try {
            const connect = new HubConnectionBuilder()
                .withUrl(config.populateFunctionAppUrl)
                .withHubProtocol(new JsonHubProtocol())
                .configureLogging(LogLevel.Information)
                .withAutomaticReconnect()
                .build();

            await connect.start();

            setConnection(connect);
        } catch (e: any) {
            console.log(e);
        }
    }

    useEffect(() => {
        getLastPopulatedDate();
        signalR();
        return () => {
            connection?.stop();
        };
    }, [keyCounter]);

    useEffect(() => {
        if (connection) {
            connection.on(`workforceProfilePopulateReceived-${WSPScorecardId}`, () => {
                setIsUpdating(true);
                setProgress(10);
            })
            connection.on(`workforceProfilePopulateStarting-${WSPScorecardId}`, () => {
                setProgress(50);
            })
            connection.on(`workforceProfilePopulateFailed-${WSPScorecardId}`, () => {
                toast.error("Update Failed");
                setProgress(0);
                setIsUpdating(false);
            })
            connection.on(`workforceProfilePopulateCompleted-${WSPScorecardId}`, () => {
                setProgress(100);
                setTimeout(function () {
                    toast.success("Successfully updated Workforce Profile Report");
                    setProgress(0);
                    setIsUpdating(false);
                    setKeyCounter(keyCounter + 1);
                }, 3000);
            })
        }
        return () => {
            connection?.stop();
        }
    }, [connection]);

    const getLastPopulatedDate = async () => {
        setIsLoading(true);
        try {
            const lastPopulatedDate = await WorkforceProfileService.getDateLastPopulated(WSPScorecardId);
            if (lastPopulatedDate.data) {
                setLastPopulatedDate(lastPopulatedDate.data);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const formattedDate: string = moment(lastPopulatedDate).format('DD MMMM YYYY [at] HH:mm');
    const lastPopulatedMessage = lastPopulatedDate != null ? `Your report was last populated on ${formattedDate}. If you have made changes to your data in Platform for this reporting period, please populate your report again.`
        : 'You have not populated your report yet. Please populate your report once you have data in Platform for this reporting period.'

    return (
        <>
            <Typography variant="h4" style={{ paddingBottom: "20px" }}>
                Workforce Profile Report
            </Typography>
            <Box>
                <Typography variant="body2" style={{ paddingBottom: "10px" }} key={`isLoading-${isLoading}`}>
                    {lastPopulatedMessage}
                    <br />
                    <Button sx={{ marginTop: '8px' }} variant="contained" hidden={isUpdating} onClick={() => {
                        populateDataUpdate();
                    }} size="small">UPDATE NOW</Button>
                    {isUpdating &&
                        <MicroLoading />
                    }
                </Typography>
                {isUpdating &&
                    <Box mt={2}>
                        <ImportProgressBar
                            value={progress}
                            progressData={progressData}
                        />
                    </Box>
                }
            </Box>
            <TabContext value={currentTab}>
                <SimpleTab currentTab={currentTab} tabsData={tabsData} noRedirect={true} onChange={handleChange} />

                <TabPanel style={{ width: '100%', padding: '0px' }} value={tabsData[0].route}>
                    <WorkforceProfilePreview isErrorReport={false} key={keyCounter} />
                </TabPanel>
                <TabPanel style={{ width: '100%', padding: '0px' }} value={tabsData[1].route}>
                    <WorkforceProfilePreview isErrorReport={true} key={keyCounter} />
                </TabPanel>
            </TabContext>
        </>
    );


}
